import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import IntelligenceTopBar from "@/components/IntelligenceTopBar";
import ArchetypeCard from "@/components/ArchetypeCard";
import { ARCHETYPES, fetchArchetypeMemory, type ArchetypeExposure, type ArchetypeKey } from "@/lib/archetypes";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const ArchetypeArchive = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exposures, setExposures] = useState<ArchetypeExposure[]>([]);
  const [active, setActive] = useState<ArchetypeKey>("deflector");

  useEffect(() => {
    if (!user) return;
    fetchArchetypeMemory(user.id, 20).then(setExposures).catch(() => {});
  }, [user]);

  const all = Object.values(ARCHETYPES);
  const exposureMap = new Map(exposures.map((e) => [e.archetype.key, e.sessions]));

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "linear-gradient(180deg, hsl(0 0% 8%) 0%, hsl(0 0% 4%) 100%)" }}
    >
      <IntelligenceTopBar />

      <div className="flex-1 max-w-5xl mx-auto w-full px-4 pt-10 pb-32">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-mono text-muted-foreground/60 hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-3 h-3" />
          Back
        </button>

        <header className="space-y-3 mb-12 max-w-2xl">
          <p className="text-[10px] uppercase tracking-[0.32em] text-cyan/70 font-mono">
            Behavioral Archetype Archive
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
            <span className="text-foreground">Recognize the </span>
            <span className="text-gradient-cyan">conversational identity.</span>
          </h1>
          <p className="text-sm text-muted-foreground/80 leading-relaxed">
            Recurring behavioral styles surfaced across rehearsal exposure.
            Not clinical labels — observational patterns.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-8">
          {/* Index */}
          <nav className="space-y-1 md:sticky md:top-24 md:self-start">
            <p className="text-[9px] font-mono tracking-[0.28em] uppercase text-muted-foreground/55 mb-3 px-2">
              Index
            </p>
            {all.map((a) => {
              const sessions = exposureMap.get(a.key) || 0;
              const isActive = active === a.key;
              return (
                <button
                  key={a.key}
                  onClick={() => setActive(a.key)}
                  className={cn(
                    "w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center justify-between gap-3 group",
                    isActive
                      ? "bg-cyan/10 border border-cyan/25"
                      : "border border-transparent hover:bg-secondary/40"
                  )}
                >
                  <span
                    className={cn(
                      "text-sm font-medium",
                      isActive ? "text-cyan" : "text-foreground/75 group-hover:text-foreground"
                    )}
                  >
                    {a.name}
                  </span>
                  {sessions > 0 && (
                    <span className="text-[9px] font-mono tracking-[0.18em] uppercase text-muted-foreground/55 shrink-0">
                      ×{sessions}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Detail */}
          <div className="space-y-4">
            {(() => {
              const a = ARCHETYPES[active];
              const sessions = exposureMap.get(a.key) || 0;
              return (
                <>
                  <ArchetypeCard archetype={a} variant="full" />
                  {sessions > 0 && (
                    <div className="rounded-lg border border-border/40 bg-card/30 px-4 py-3">
                      <p className="text-[9px] font-mono tracking-[0.24em] uppercase text-cyan/55 mb-1">
                        Personal Exposure
                      </p>
                      <p className="text-sm text-foreground/80 font-light">
                        Recognized in {sessions} of your recent rehearsal{sessions > 1 ? "s" : ""}.
                      </p>
                    </div>
                  )}
                  <button
                    onClick={() =>
                      navigate("/rehearsal", {
                        state: {
                          entry_type: "personal",
                          patterns: [{ name: a.name, score: 7 }],
                          seed_context: a.phrases[0],
                        },
                      })
                    }
                    className="w-full mt-2 px-4 py-3 rounded-xl text-sm font-semibold tracking-wide bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground hover:scale-[1.005] transition-transform shadow-[0_0_30px_-12px_hsl(180_100%_50%_/_0.5)]"
                  >
                    Rehearse against {a.name}
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchetypeArchive;
